import { useState, type MouseEvent } from "react";
import Badge from "@mui/material/Badge";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

/** antd column-filter option; `children` makes it a tree node (antd filterMode: "tree"). */
export interface AntdFilterOption {
  text: string;
  value: string | number | boolean;
  children?: AntdFilterOption[];
}

export type FilterValue = string | number | boolean;

// Flatten the antd tree into rows carrying their depth so the menu renders an indented
// list. antd drew a real collapsible tree; an indented checkbox list makes the same
// selection with a fraction of the machinery.
// ponytail: no collapse/expand — the deepest caller is strategy/sub-strategy/list. Add
// it if something ever nests deeper than that.
const flattenOptions = (
  options: AntdFilterOption[],
  depth = 0,
): Array<{ option: AntdFilterOption; depth: number }> =>
  options.flatMap((option) => [
    { option, depth },
    ...(option.children ? flattenOptions(option.children, depth + 1) : []),
  ]);

// Ticking a parent means "everything beneath it" — antd's tree filter selects the whole
// subtree. Parents stay in the selection alongside their leaves, because a caller may
// define onFilter against the parent value too.
const withDescendants = (option: AntdFilterOption): FilterValue[] => [
  option.value,
  ...(option.children ?? []).flatMap(withDescendants),
];

interface Props {
  options: AntdFilterOption[];
  selected: FilterValue[];
  onChange: (next: FilterValue[]) => void;
  /** Column header text, for the control's accessible name. */
  label: string;
}

/** antd's column filter dropdown. MUI's own filter UI lives in the column menu, which
 *  DataTable disables for antd parity — so the affordance is this header button. */
export const ColumnFilterButton = ({ options, selected, onChange, label }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<FilterValue[]>(selected);

  const open = (event: MouseEvent<HTMLElement>) => {
    // The header is also the sort control — don't sort while opening the filter.
    event.stopPropagation();
    setDraft(selected);
    setAnchorEl(event.currentTarget);
  };
  const close = () => setAnchorEl(null);

  const toggle = (option: AntdFilterOption) => {
    const affected = withDescendants(option);
    // Functional updater, not the `draft` closure: two ticks inside one React batch
    // would both read the pre-batch value and the second would discard the first.
    setDraft((prev) =>
      prev.includes(option.value)
        ? prev.filter((v) => !affected.includes(v))
        : [...prev, ...affected.filter((v) => !prev.includes(v))],
    );
  };

  const apply = () => { onChange(draft); close(); };
  const reset = () => { onChange([]); close(); };

  return (
    <>
      <Badge color="primary" variant="dot" invisible={selected.length === 0}>
        <IconButton
          size="small"
          onClick={open}
          aria-label={`Filter ${label}`}
          data-cy="column-filter-button"
        >
          <FilterAltIcon fontSize="inherit" color={selected.length ? "primary" : "action"} />
        </IconButton>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        slotProps={{ paper: { sx: { maxHeight: 420 } } }}
      >
        {flattenOptions(options).map(({ option, depth }) => (
          <MenuItem key={String(option.value)} dense onClick={() => toggle(option)} sx={{ pl: 1 + depth * 2 }}>
            <Checkbox size="small" checked={draft.includes(option.value)} sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primary={option.text} primaryTypographyProps={{ variant: "body2" }} />
          </MenuItem>
        ))}
        <Divider />
        <Stack direction="row" justifyContent="space-between" sx={{ px: 1, py: 0.5 }}>
          <Button size="small" onClick={reset} disabled={selected.length === 0 && draft.length === 0}>
            Reset
          </Button>
          <Button size="small" variant="contained" onClick={apply}>OK</Button>
        </Stack>
      </Menu>
    </>
  );
};

export default ColumnFilterButton;
